import Credit from '../credit/Credit'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronRight,
  faClock,
  faGlobe,
  faPhone,
  faTruckFast,
} from '@fortawesome/free-solid-svg-icons'
import InstagramIcon from '../../assets/icons/instagram.svg?react'
import { useAppNavigate, useAppType } from '../../context/AppContext'
import { useGetAppStatusQuery } from '../../store/api/appStatusApi'
import { BRANDS } from '../../utils/brand'
import { INSTAGRAM_LINK } from '../../utils/constants'
import { getGeneralInfo } from '../../utils/generalInfo'
import BottomSheet from '../bottom-sheet/BottomSheet'
import LanguageToggle from '../language-toggle/LanguageToggle'
import StatusPill from '../status-pill/StatusPill'
import './InfoSheet.scss'

type Props = {
  open: boolean
  onClose: () => void
}

const c = 'info-sheet'

const InfoSheet = ({ open, onClose }: Props) => {
  const { t } = useTranslation()
  const navigate = useAppNavigate()
  const navigateRaw = useNavigate()
  const appType = useAppType()
  const brand = BRANDS[appType]
  const { data: appStatus } = useGetAppStatusQuery()
  const info = getGeneralInfo()
  const phones = [info.phone1, info.phone2].filter(Boolean) as string[]

  const otherApp = appType === 'sushi' ? 'catering' : 'sushi'
  const otherBrand = BRANDS[otherApp]
  const otherAppDisabled =
    appType === 'sushi'
      ? (appStatus?.isCateringDisabled ?? false)
      : (appStatus?.isSushiDisabled ?? false)

  return (
    <BottomSheet open={open} onClose={onClose} className={c}>
      <div className={`${c}__hero`}>
        <img src={brand.mascot} alt='' />
        <div>
          <div className={`${c}__brand`}>
            {brand.first} <b>{brand.second}</b>
          </div>
          <StatusPill />
        </div>
      </div>

      <div className={`${c}__card`}>
        <FontAwesomeIcon icon={faClock} className={`${c}__card-icon`} />
        <div>
          <div className={`${c}__label`}>{t('SCHEDULE')}</div>
          <div className={`${c}__value`}>
            {info.minTime} – {info.maxTime}
          </div>
        </div>
      </div>

      <div className={`${c}__label ${c}__label--section`}>{t('CALL_US')}</div>
      <div className={`${c}__phones`}>
        {phones.map((phone) => (
          <a key={phone} href={`tel:${phone}`} className={`${c}__phone`}>
            <FontAwesomeIcon icon={faPhone} />
            {phone}
          </a>
        ))}
      </div>

      <button
        className={`${c}__row`}
        onClick={() => {
          onClose()
          navigate('/delivery-conditions')
        }}
      >
        <FontAwesomeIcon icon={faTruckFast} className={`${c}__row-icon`} />
        <span>{t('DELIVERY_CONDITIONS')}</span>
        <FontAwesomeIcon icon={faChevronRight} className={`${c}__chevron`} />
      </button>

      <a
        className={`${c}__row`}
        href={INSTAGRAM_LINK}
        target='_blank'
        rel='noreferrer'
      >
        <span className={`${c}__row-icon ${c}__row-icon--insta`}>
          <InstagramIcon />
        </span>
        <span>Instagram</span>
        <FontAwesomeIcon icon={faChevronRight} className={`${c}__chevron`} />
      </a>

      <div className={`${c}__row ${c}__row--static`}>
        <span className={`${c}__row-icon`}>
          <FontAwesomeIcon icon={faGlobe} />
        </span>
        <span>{t('LANGUAGE')}</span>
        <LanguageToggle variant='soft' />
      </div>

      {!otherAppDisabled && (
        <button
          className={`${c}__switch ${c}__switch--${otherApp}`}
          onClick={() => {
            onClose()
            navigateRaw(`/${otherApp}`)
          }}
        >
          <img src={otherBrand.mascot} alt='' />
          <div>
            <small>{t('SWITCH_APP')}</small>
            <strong>
              {otherBrand.first}
              {otherBrand.second}
            </strong>
          </div>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}

      <div className={`${c}__credit`}>
        <Credit />
      </div>
    </BottomSheet>
  )
}

export default InfoSheet
